import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
/* eslint-disable @typescript-eslint/naming-convention,react/no-danger */
import { useContext, useEffect, useMemo, useCallback, useRef, useState, } from 'preact/compat';
import { useFormContext, } from 'react-hook-form';
import clsx from 'clsx';
import DOMPurify from 'dompurify';
import useGlobalEvent from 'beautiful-react-hooks/useGlobalEvent';
import defaultNumberFormat from '@widgets/shared/defaultNumberFormat';
import QuickPay from '@widgets/shared/components/QuickPay';
import { TermsWrapper } from '@bookingForm/styles/misc';
import { ConfigCTX } from '@basket/context/config';
import { useAppDispatch, useBasketSelector } from '@basket/store/hooks';
import { getQuickPayAppleState, getQuickPayGoogleState, setQuickPayState } from '@basket/store/reducers/quickPay';
import TermsModal from '@widgets/shared/components/TermsModal';
import { getTermsContent, getTermsHeader, getTermsSubheader, setTermsContent, setTermsHeader, setTermsSubheader, } from '@basket/store/reducers/termsAndConditions';
import TermsCheckbox from '@genericBookings/components/TermsCheckbox/TermsCheckbox';
function BasketOverview(props) {
    const { items, misc, totalExtraContent, total, } = props;
    const [modalOpen, setModalOpen] = useState(false);
    const [shouldShakeTerms, setShouldShakeTerms] = useState(false);
    const animationLengthInMilliseconds = 1000;
    const termsRef = useRef(null);
    const handleOpen = useCallback(() => setModalOpen(true), [setModalOpen]);
    const handleClose = useCallback(() => setModalOpen(false), [setModalOpen]);
    const dispatch = useAppDispatch();
    const appleHidden = useBasketSelector(getQuickPayAppleState);
    const googleHidden = useBasketSelector(getQuickPayGoogleState);
    const termsHeading = useBasketSelector(getTermsHeader);
    const termsContent = useBasketSelector(getTermsContent);
    const termsSubheading = useBasketSelector(getTermsSubheader);
    const { termsHeading: initialTermsHeading, termsSubheading: initialTermsSubheading, termsContent: initialTermsContent, termsShowCheckbox, hideQuickPayButtons, darkQuickPayButtons, isModal, requiresSignature, } = useContext(ConfigCTX);
    useEffect(() => {
        dispatch(setTermsHeader(initialTermsHeading ?? ''));
        dispatch(setTermsContent(initialTermsContent ?? ''));
        dispatch(setTermsSubheader(initialTermsSubheading ?? ''));
    }, [
        dispatch,
        initialTermsContent,
        initialTermsHeading,
        initialTermsSubheading,
    ]);
    const form = useFormContext();
    const { register, formState: { isValid, touchedFields }, watch, setValue, } = form;
    const quickPayBasket = useMemo(() => ({
        type: 'basket',
        misc,
        items,
    }), [items, misc]);
    const termsAccepted = Boolean(watch('acceptTerms'));
    const isFormValid = useMemo(() => isValid && termsAccepted, [isValid, termsAccepted]);
    const buttonClasses = useMemo(() => clsx('btn', !isFormValid && 'btn--disabled'), [isFormValid]);
    const termsClasses = useMemo(() => clsx('terms', shouldShakeTerms && 'shake'), [shouldShakeTerms]);
    const handleClick = useCallback((e) => {
        if (!isFormValid) {
            e.preventDefault();
            // Only shake the terms if not already shaking
            if (!shouldShakeTerms) {
                setShouldShakeTerms(true);
                setTimeout(() => {
                    setShouldShakeTerms(false);
                }, animationLengthInMilliseconds);
            }
        }
    }, [isFormValid, shouldShakeTerms]);
    const handleTermsChanged = useCallback(() => {
        if (touchedFields && touchedFields.acceptTerms
            && termsAccepted
            && requiresSignature) {
            setModalOpen(true);
        }
    }, [
        touchedFields,
        termsAccepted,
        requiresSignature,
    ]);
    const renderTerms = useCallback(() => {
        if (!termsShowCheckbox) {
            return null;
        }
        if (!isModal) {
            return (_jsx(TermsWrapper, { className: termsClasses, ref: termsRef, children: _jsx(TermsCheckbox, { name: "acceptTerms", onLinkClicked: handleOpen }) }));
        }
        return null;
    }, [
        handleOpen,
        isModal,
        termsClasses,
        termsShowCheckbox,
    ]);
    const handleTermsCancelled = useCallback(() => {
        setValue('acceptTerms', false, { shouldValidate: true });
        setModalOpen(false);
    }, [setValue]);
    const handleTermsConfirmed = useCallback(() => {
        setValue('acceptTerms', true, { shouldValidate: true });
        setModalOpen(false);
    }, [setValue]);
    const onAppleAvailable = useGlobalEvent('apple.available');
    const onGoogleAvailable = useGlobalEvent('google.available');
    onAppleAvailable(() => setQuickPayState('apple', false));
    onGoogleAvailable(() => setQuickPayState('google', false));
    useEffect(handleTermsChanged, [termsAccepted, handleTermsChanged]);
    return (_jsxs("div", { className: "basket__overview-wrapper", children: [_jsx("input", { type: "hidden", ...register('SignatureName') }), _jsx("input", { type: "hidden", ...register('SignatureDate') }), (modalOpen && termsContent) && (_jsx(TermsModal, { name: "acceptTerms", onCancel: handleTermsCancelled, onConfirm: handleTermsConfirmed, termsHeading: termsHeading ?? 'Terms & Conditions for recreational activities', termsSubheading: termsSubheading ?? 'Please read the following and accept below:', termsContent: termsContent ?? '', termsShowCheckbox: false, showSignatureField: requiresSignature })), _jsxs("div", { className: "overview", style: { marginBottom: isModal ? 20 : 0 }, children: [_jsx("span", { className: "overview__total", children: `${defaultNumberFormat.format(total)}` }), totalExtraContent && (_jsx("div", { className: "overview__extra", dangerouslySetInnerHTML: { __html: DOMPurify.sanitize(totalExtraContent) } })), !hideQuickPayButtons && (_jsx(QuickPay, { darkButtons: darkQuickPayButtons, products: quickPayBasket, termsAccepted: termsAccepted, onClickedWhenDisabled: () => { }, appleHidden: appleHidden, googleHidden: googleHidden, handleAppleDidFail: (status) => dispatch(setQuickPayState('apple', status)), handleGoogleDidFail: (status) => dispatch(setQuickPayState('google', status)) })), _jsx("button", { type: "submit", onClick: handleClick, className: buttonClasses, children: "Checkout" }), renderTerms()] })] }));
}
BasketOverview.defaultProps = {
    totalExtraContent: undefined,
    layout: 'bookings',
};
export default BasketOverview;
